import { useEffect } from 'react';

import { getDocument } from '@change-corgi/core/window';

import { listenToElementRemoval } from '../shared/listenToElementRemoval';

import { useFocusFallbackTrigger } from './useFocusFallbackTrigger';

export function useFocusFallbackListener(): void {
	const focusFallback = useFocusFallbackTrigger();

	useEffect(() => {
		if (!focusFallback) {
			return undefined;
		}

		let cleanListener: (() => void) | undefined;

		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		const { activeElement } = getDocument() || {};

		if (activeElement && !cleanListener) {
			// if there is already a focused element when hydrating, let's listen to it
			cleanListener = listenToElementRemoval(activeElement, focusFallback);
		}

		const listener = (event: FocusEvent) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const focusElt = event.target as Node | null;

			if (!focusElt) return;

			// stop observing previous focus element
			cleanListener?.();

			cleanListener = listenToElementRemoval(focusElt, focusFallback);
		};

		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		getDocument() && getDocument().addEventListener('focusin', listener);

		return () => {
			cleanListener?.();

			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			getDocument() && getDocument().removeEventListener('focusin', listener);
		};
	}, [focusFallback]);
}
